/* Copy da gestão de staff (ADR-0709): tela restrita a super_admin, tabela de operadores e diálogo
   de suspensão/reativação (mesma frase serve os dois sentidos, só o verbo muda). */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface StaffContent {
  readonly pageTitle: string;
  readonly restrictedDescription: string;
  readonly restrictedAlert: string;
  readonly pageDescription: string;
  readonly newMemberButton: string;
  readonly infoAlert: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly suspendSuccessToast: string;
  readonly reactivateSuccessToast: string;
  readonly suspendActionLabel: string;
  readonly reactivateActionLabel: string;
  readonly suspendDialogTitle: string;
  readonly reactivateDialogTitle: string;
  /** Descrição do diálogo: o verbo (Suspender/Reativar) já traduzido entra como parâmetro. */
  readonly confirmDescription: (actionLabel: string, email: string, statusLabel: string) => string;
  readonly bulk: {
    readonly selected: (count: number) => string;
    readonly suspend: string;
    readonly reactivate: string;
    readonly clear: string;
    readonly suspendTitle: string;
    readonly reactivateTitle: string;
    readonly description: (action: string, count: number) => string;
    readonly success: (count: number) => string;
    readonly partial: (success: number, failed: number) => string;
  };
}

const STAFF_CONTENT: Bilingual<StaffContent> = {
  'pt-BR': {
    pageTitle: 'Equipe',
    restrictedDescription: 'Operadores do backoffice.',
    restrictedAlert: 'Apenas super admins podem gerenciar a equipe.',
    pageDescription: 'Operadores do backoffice — papéis, status e reset de senha.',
    newMemberButton: 'Novo membro',
    infoAlert:
      'Novos operadores nascem com uma senha inicial que você define. Não é possível deixar a ' +
      'plataforma sem nenhum super admin ativo, nem suspender a própria conta. Ações ficam na ' +
      'trilha de auditoria.',
    loadError: 'Falha ao carregar a equipe.',
    loadMoreButton: 'Carregar mais',
    suspendSuccessToast: 'Operador suspenso.',
    reactivateSuccessToast: 'Operador reativado.',
    suspendActionLabel: 'Suspender',
    reactivateActionLabel: 'Reativar',
    suspendDialogTitle: 'Suspender operador',
    reactivateDialogTitle: 'Reativar operador',
    confirmDescription: (actionLabel, email, statusLabel) =>
      `${actionLabel} "${email}"? Status atual: ${statusLabel}.`,
    bulk: {
      selected: (count) => `${count} operador(es) selecionado(s)`,
      suspend: 'Suspender',
      reactivate: 'Reativar',
      clear: 'Limpar seleção',
      suspendTitle: 'Suspender operadores selecionados',
      reactivateTitle: 'Reativar operadores selecionados',
      description: (action, count) =>
        `${action} ${count} operador(es)? Os guards de acesso serão avaliados por item.`,
      success: (count) => `Status atualizado para ${count} operador(es).`,
      partial: (success, failed) => `${success} atualizado(s); ${failed} com falha.`,
    },
  },
  'en-US': {
    pageTitle: 'Staff',
    restrictedDescription: 'Backoffice operators.',
    restrictedAlert: 'Only super admins can manage the staff.',
    pageDescription: 'Backoffice operators — roles, status and password reset.',
    newMemberButton: 'New member',
    infoAlert:
      'New operators start with an initial password you set. The platform cannot be left ' +
      'without an active super admin, and you cannot suspend your own account. Actions are ' +
      'tracked in the audit trail.',
    loadError: 'Failed to load the staff.',
    loadMoreButton: 'Load more',
    suspendSuccessToast: 'Operator suspended.',
    reactivateSuccessToast: 'Operator reactivated.',
    suspendActionLabel: 'Suspend',
    reactivateActionLabel: 'Reactivate',
    suspendDialogTitle: 'Suspend operator',
    reactivateDialogTitle: 'Reactivate operator',
    confirmDescription: (actionLabel, email, statusLabel) =>
      `${actionLabel} "${email}"? Current status: ${statusLabel}.`,
    bulk: {
      selected: (count) => `${count} operator(s) selected`,
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      clear: 'Clear selection',
      suspendTitle: 'Suspend selected operators',
      reactivateTitle: 'Reactivate selected operators',
      description: (action, count) =>
        `${action} ${count} operator(s)? Access guards are evaluated per item.`,
      success: (count) => `Status updated for ${count} operator(s).`,
      partial: (success, failed) => `${success} updated; ${failed} failed.`,
    },
  },
};

export const useStaffContent = (): StaffContent => useBilingual(STAFF_CONTENT);
